import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Image, Text, TouchableOpacity, View } from 'react-native';
import { ThemeColors } from '@/src/context/ThemeContext';
import { Team, TeamInvitation, TeamMember } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { PatternBackground } from '@/src/presentation/components';
import { MembersTab } from './tabs/MembersTab';
import { SettingsTab } from './tabs/SettingsTab';
import { createStyles } from './TeamDetailScreen.styles';

type TabKey = 'members' | 'settings';

interface TeamDetailScreenProps {
  team: Team;
  members: TeamMember[];
  pendingInvitations: TeamInvitation[];
  isCaptain: boolean;
  isCurrentMember: boolean;
  currentUserId: string;
  inviting: boolean;
  updating: boolean;
  uploadingLogo: boolean;
  editName: string;
  editDescription: string;
  editIsPublic: boolean;
  onEditNameChange: (value: string) => void;
  onEditDescriptionChange: (value: string) => void;
  onEditIsPublicChange: (value: boolean) => void;
  onInviteMember: (email: string) => void;
  onCancelInvitation: (invitationId: string) => void;
  onRemoveMember: (userId: string) => void;
  onLeaveTeam: () => void;
  onDeleteTeam: () => void;
  onSaveSettings: () => void;
  onUploadLogo: () => void;
  onNavigateToProfile: (userId: string) => void;
  onSearchUser: (query: string) => Promise<Array<{ id: string; email: string; name?: string }>>;
  colors: ThemeColors;
}

export const TeamDetailScreen: React.FC<TeamDetailScreenProps> = ({
  team,
  members,
  pendingInvitations,
  isCaptain,
  isCurrentMember,
  currentUserId,
  inviting,
  updating,
  uploadingLogo,
  editName,
  editDescription,
  editIsPublic,
  onEditNameChange,
  onEditDescriptionChange,
  onEditIsPublicChange,
  onInviteMember,
  onCancelInvitation,
  onRemoveMember,
  onLeaveTeam,
  onDeleteTeam,
  onSaveSettings,
  onUploadLogo,
  onNavigateToProfile,
  onSearchUser,
  colors,
}) => {
  const { t } = useTranslation();
  const styles = createStyles(colors);
  const [activeTab, setActiveTab] = useState<TabKey>('members');

  const tabs: { key: TabKey; label: string }[] = [
    { key: 'members', label: t('teams.members') },
    ...(isCaptain ? [{ key: 'settings' as TabKey, label: t('teams.settings') }] : []),
  ];

  return (
    <PatternBackground>
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.logoCircle}>
          {team.logoUrl ? (
            <Image source={{ uri: team.logoUrl }} style={styles.logoImage} />
          ) : (
            <Ionicons name="people" size={32} color={colors.textTertiary} />
          )}
        </View>
        <Text style={styles.teamName}>{team.name}</Text>
        {team.description ? (
          <Text style={styles.teamDescription} numberOfLines={2}>
            {team.description}
          </Text>
        ) : null}
      </View>

      {/* Tab bar */}
      <View style={styles.tabBar}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, isActive && styles.tabActive]}
              onPress={() => setActiveTab(tab.key)}
            >
              <Text style={[styles.tabText, isActive && styles.tabTextActive]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </View>

      {/* Tab content */}
      {activeTab === 'members' && (
        <MembersTab
          members={members}
          pendingInvitations={pendingInvitations}
          captainId={team.userId}
          currentUserId={currentUserId}
          isCaptain={isCaptain}
          inviting={inviting}
          onInviteMember={onInviteMember}
          onCancelInvitation={onCancelInvitation}
          onRemoveMember={onRemoveMember}
          onLeaveTeam={onLeaveTeam}
          onNavigateToProfile={onNavigateToProfile}
          onSearchUser={onSearchUser}
          colors={colors}
        />
      )}

      {activeTab === 'settings' && (
        <SettingsTab
          team={team}
          isCaptain={isCaptain}
          isCurrentMember={isCurrentMember}
          editName={editName}
          editDescription={editDescription}
          editIsPublic={editIsPublic}
          updating={updating}
          uploadingLogo={uploadingLogo}
          onEditNameChange={onEditNameChange}
          onEditDescriptionChange={onEditDescriptionChange}
          onEditIsPublicChange={onEditIsPublicChange}
          onSave={onSaveSettings}
          onUploadLogo={onUploadLogo}
          onLeaveTeam={onLeaveTeam}
          onDeleteTeam={onDeleteTeam}
          colors={colors}
        />
      )}
    </View>
    </PatternBackground>
  );
};
